import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import WellnessFilterPanel from './components/WellnessFilterPanel';
import WellnessCenterCard from './components/WellnessCenterCard';
import WellnessMapView from './components/WellnessMapView';
import SortDropdown from './components/SortDropdown';
import LoadingState from './components/LoadingState';

const WellnessCenterListings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map' | 'split'
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [centers, setCenters] = useState([]);
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);

  // Mock data - in real app would come from API
  const mockCenters = [
    {
      id: 1,
      name: "Sedona Energy Healing Center",
      location: "Sedona, Arizona",
      coordinates: { lat: 34.8697, lng: -111.7610 },
      image: "/assets/images/sedona-center.jpg",
      rating: 4.9,
      reviewCount: 324,
      priceFrom: 280,
      type: "EES Center",
      categories: ["energy-enhancement", "chakra-balancing", "crystal-therapy"],
      amenities: ["EES Technology", "Crystal Healing", "Meditation Garden", "Energy Assessment"],
      practitioner: "Dr. Sarah Moonwhisper",
      certifications: ["EES Certified", "Reiki Master"],
      description: "Experience profound energy healing in Sedona's powerful vortex locations.",
      programs: ["EES Sessions", "Chakra Alignment", "Crystal Healing", "Energy Assessment"],
      duration: "Half Day",
      availability: "available",
      featured: true
    },
    {
      id: 2,
      name: "Tulum Wellness Sanctuary",
      location: "Tulum, Mexico",
      coordinates: { lat: 20.2114, lng: -87.4654 },
      image: "/assets/images/tulum-sanctuary.jpg",
      rating: 4.8,
      reviewCount: 198,
      priceFrom: 320,
      type: "Meditation Retreat",
      categories: ["meditation", "sound-healing", "cacao-ceremony"],
      amenities: ["Beachfront Location", "Sound Baths", "Yoga Shalas", "Cacao Ceremonies"],
      practitioner: "Maya Celestial",
      certifications: ["Sound Therapy Certified", "Cacao Ceremonial Leader"],
      description: "Reconnect with your inner wisdom in this pristine beachfront sanctuary.",
      programs: ["Sound Healing", "Cacao Ceremonies", "Beach Meditation", "Temazcal"],
      duration: "3 Days",
      availability: "available",
      featured: true
    },
    {
      id: 3,
      name: "Mount Shasta Healing Arts",
      location: "Mount Shasta, California",
      coordinates: { lat: 41.4099, lng: -122.1944 },
      image: "/assets/images/mount-shasta.jpg",
      rating: 4.9,
      reviewCount: 287,
      priceFrom: 240,
      type: "Crystal Therapy",
      categories: ["crystal-therapy", "energy-work", "forest-bathing"],
      amenities: ["Sacred Mountain Views", "Lemurian Crystals", "Forest Bathing", "Energy Work"],
      practitioner: "Jonathan Starseed",
      certifications: ["Crystal Master", "Lemurian Healing"],
      description: "Harness the mystical energy of sacred Mount Shasta for deep healing.",
      programs: ["Crystal Healing", "Lemurian Light Work", "Forest Immersion", "Sacred Geometry"],
      duration: "Full Day",
      availability: "limited",
      featured: true
    },
    {
      id: 4,
      name: "Big Sur Mindfulness Retreat",
      location: "Big Sur, California",
      coordinates: { lat: 36.2704, lng: -121.8081 },
      image: "/assets/images/big-sur.jpg",
      rating: 4.7,
      reviewCount: 156,
      priceFrom: 380,
      type: "Spa Resort",
      categories: ["mindfulness", "spa-therapy", "ocean-healing"],
      amenities: ["Ocean Views", "Hot Springs", "Massage Therapy", "Meditation Trails"],
      practitioner: "Elena Pacific",
      certifications: ["Mindfulness Instructor", "Nature Therapy"],
      description: "Find peace and renewal overlooking the dramatic Pacific coastline.",
      programs: ["Mindfulness Training", "Ocean Therapy", "Hot Springs", "Massage Therapy"],
      duration: "Weekend",
      availability: "available",
      featured: false
    },
    {
      id: 5,
      name: "Rishikesh Spiritual Center",
      location: "Rishikesh, India",
      coordinates: { lat: 30.0869, lng: 78.2676 },
      image: "/assets/images/rishikesh.jpg",
      rating: 5.0,
      reviewCount: 445,
      priceFrom: 150,
      type: "Yoga Retreat",
      categories: ["yoga", "pranayama", "vedic-studies"],
      amenities: ["Ganges River Views", "Ancient Traditions", "Pranayama", "Vedic Studies"],
      practitioner: "Swami Anandamaya",
      certifications: ["500-Hour RYT", "Pranayama Master"],
      description: "Immerse yourself in authentic yogic traditions by the sacred Ganges.",
      programs: ["Hatha Yoga", "Pranayama", "Meditation", "Vedic Philosophy"],
      duration: "7 Days",
      availability: "available",
      featured: false
    },
    {
      id: 6,
      name: "Bali Healing Village",
      location: "Ubud, Indonesia",
      coordinates: { lat: -8.5069, lng: 115.2625 },
      image: "/assets/images/ubud-healing.jpg",
      rating: 4.8,
      reviewCount: 267,
      priceFrom: 195,
      type: "Detox Center",
      categories: ["detox", "balinese-healing", "water-blessing"],
      amenities: ["Rice Terrace Views", "Balinese Healing", "Raw Food Cuisine", "Water Blessings"],
      practitioner: "Kadek Spiritual",
      certifications: ["Traditional Balinese Healer", "Water Ceremony Guide"],
      description: "Experience traditional Balinese healing in the heart of Ubud\'s jungle.",
      programs: ["Detox Programs", "Water Blessings", "Balinese Massage", "Herbal Medicine"],
      duration: "5 Days",
      availability: "available",
      featured: false
    }
  ];

  useEffect(() => {
    // Simulate API loading
    const timer = setTimeout(() => {
      setCenters(mockCenters);
      setFilteredCenters(mockCenters);
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Handle URL parameters
  useEffect(() => {
    const query = searchParams.get('query');
    const location = searchParams.get('location');
    const type = searchParams.get('type');
    const category = searchParams.get('category');

    if (query || location || type || category) {
      // Apply filters based on URL parameters
      applyFilters({ query, location, type, category });
    }
  }, [searchParams]);

  const applyFilters = (filters) => {
    let filtered = [...centers];

    if (filters.query) {
      filtered = filtered.filter(center =>
        center.name.toLowerCase().includes(filters.query.toLowerCase()) ||
        center.description.toLowerCase().includes(filters.query.toLowerCase()) ||
        center.programs?.some(program =>
          program.toLowerCase().includes(filters.query.toLowerCase())
        )
      );
    }

    if (filters.location) {
      filtered = filtered.filter(center =>
        center.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    if (filters.type) {
      filtered = filtered.filter(center =>
        center.type.toLowerCase().includes(filters.type.toLowerCase()) ||
        center.categories?.includes(filters.type)
      );
    }

    if (filters.category) {
      filtered = filtered.filter(center =>
        center.categories?.includes(filters.category) ||
        center.type.toLowerCase().includes(filters.category.toLowerCase())
      );
    }

    setFilteredCenters(filtered);
  };

  const handleSort = (sortBy) => {
    let sorted = [...filteredCenters];

    switch (sortBy) {
      case 'price-low':
        sorted.sort((a, b) => a.priceFrom - b.priceFrom);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.priceFrom - a.priceFrom);
        break;
      case 'rating':
        sorted.sort((a, b) => b.rating - a.rating);
        break;
      case 'reviews':
        sorted.sort((a, b) => b.reviewCount - a.reviewCount);
        break;
      case 'newest':
        // In real app, would sort by creation date
        sorted.sort((a, b) => b.id - a.id);
        break;
      default:
        // Featured first, then by rating
        sorted.sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return b.rating - a.rating;
        });
    }

    setFilteredCenters(sorted);
  };

  const handleCenterSelect = (center) => {
    setSelectedCenter(center);
    if (viewMode === 'list') {
      setViewMode('split');
    }
  };

  const getViewModeClasses = () => {
    switch (viewMode) {
      case 'map':
        return 'lg:grid-cols-1';
      case 'split':
        return 'lg:grid-cols-2';
      default:
        return 'lg:grid-cols-1';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <LoadingState />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Main Content */}
      <div className="pt-24 pb-8">
        {/* Header Section */}
        <div className="px-4 sm:px-6 lg:px-8 mb-6">
          <div className="max-w-7xl mx-auto">
            {/* Search Summary */}
            <div className="mb-6">
              <h1 className="text-2xl lg:text-3xl font-bold text-text-primary mb-2 font-heading">
                Wellness Centers & Retreats
              </h1>
              <p className="text-text-secondary">
                {filteredCenters.length} centers available
                {searchParams.get('location') && ` in ${searchParams.get('location')}`}
              </p>
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center
                           gap-4">
              <div className="flex items-center space-x-4">
                {/* Filter Toggle */}
                <button
                  onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                  className="flex items-center space-x-2 px-4 py-2 bg-white border border-border
                             rounded-lg hover:bg-background-light hover:border-border-focus transition-all
                             duration-200 shadow-elevation-1"
                >
                  <Icon name="Filter" size={16} />
                  <span className="font-medium">Filters</span>
                  {isFilterPanelOpen && (
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  )}
                </button>

                {/* View Toggle - Desktop */}
                <div className="hidden md:flex items-center space-x-1 bg-white border border-border rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      viewMode === 'list' ?'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-background-light' // Changed from blue-600 and blue-50
                    }`}
                  >
                    <Icon name="List" size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('split')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      viewMode === 'split' ?'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-background-light' // Changed from blue-600 and blue-50
                    }`}
                  >
                    <Icon name="LayoutGrid" size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('map')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      viewMode === 'map' ?'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-background-light' // Changed from blue-600 and blue-50
                    }`}
                  >
                    <Icon name="Map" size={16} />
                  </button>
                </div>

                {/* View Toggle - Mobile */}
                <div className="md:hidden">
                  <button
                    onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
                    className="flex items-center space-x-2 px-4 py-2 bg-white border border-border
                               rounded-lg hover:bg-background-light hover:border-border-focus transition-all
                               duration-200 shadow-elevation-1"
                  >
                    <Icon name={viewMode === 'list' ? 'Map' : 'List'} size={16} />
                    <span className="font-medium">
                      {viewMode === 'list' ? 'Map' : 'List'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Sort Dropdown */}
              <SortDropdown onSort={handleSort} />
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className={`px-4 sm:px-6 lg:px-8 grid ${getViewModeClasses()} gap-6 max-w-7xl mx-auto`}>
          {/* Filter Panel */}
          {isFilterPanelOpen && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="lg:col-span-1 order-1"
            >
              <WellnessFilterPanel
                onFilterChange={applyFilters}
                centers={centers}
                onClose={() => setIsFilterPanelOpen(false)}
              />
            </motion.div>
          )}

          {/* Listings */}
          {viewMode !== 'map' && (
            <div className={`${isFilterPanelOpen ? 'lg:col-span-1' : 'lg:col-span-1'} order-2`}>
              <div className="space-y-6">
                {filteredCenters.length > 0 ? (
                  filteredCenters.map((center) => (
                    <WellnessCenterCard
                      key={center.id}
                      center={center}
                      onSelect={() => handleCenterSelect(center)}
                      isSelected={selectedCenter?.id === center.id}
                    />
                  ))
                ) : (
                  <div className="text-center py-16">
                    <Icon name="Search" size={64} className="text-text-secondary mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-text-primary mb-2">
                      No centers found
                    </h3>
                    <p className="text-text-secondary">
                      Try adjusting your filters or search criteria
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Map */}
          {viewMode !== 'list' && (
            <div className={`${viewMode === 'split' ? 'lg:col-span-1' : 'lg:col-span-1'} order-3`}>
              <div className="sticky top-24">
                <WellnessMapView
                  centers={filteredCenters}
                  selectedCenter={selectedCenter}
                  onCenterSelect={handleCenterSelect}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WellnessCenterListings;
